import type { TFunctionType } from "@/core/hooks/use-typed-translation";
import { getCurrentLanguage } from "@/core/utils/func/language-resolver";
import { format, parseISO } from "date-fns";
import { enUS, fr, zhCN, type Locale } from "date-fns/locale";
import type {
  InterbankAccountFallback,
  ParsedRequestBody,
  RequestFlexCubeBody,
  TransactionDetailData,
  TransactionStatus,
  TransactionType,
} from "./types";

export type TranslateFn = (
  key: TFunctionType,
  params?: Record<string, unknown>
) => string;

const DATE_FNS_LOCALES: Record<string, Locale> = {
  fr,
  en: enUS,
  zh: zhCN,
};

export const formatKey = (key: string): string =>
  key
    .replace(/[_-]/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

export const isPlainObject = (
  value: unknown
): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

export const isPrimitive = (
  value: unknown
): value is string | number | boolean =>
  ["string", "number", "boolean"].includes(typeof value);

export const formatDateTime = (
  date: string | undefined | null,
  t: TranslateFn
): string => {
  if (!date) return "";
  try {
    return format(parseISO(date), `dd MMMM yyyy '${t("at")}' HH:mm`, {
      locale: DATE_FNS_LOCALES[getCurrentLanguage()] ?? fr,
    });
  } catch {
    return date;
  }
};

export const isTransactionDetailData = (
  data: unknown
): data is TransactionDetailData => typeof data === "object" && data !== null;

export function parseRequestBody(
  requestBody: string | undefined
): ParsedRequestBody | null {
  if (!requestBody) return null;
  try {
    return JSON.parse(requestBody) as ParsedRequestBody;
  } catch {
    return null;
  }
}

export function getInterbankAccountFallback(data: TransactionDetailData): {
  debit: InterbankAccountFallback;
  beneficiary: InterbankAccountFallback;
} {
  const body =
    data.requestFlexCube?.TRANSACTION_REQUEST?.REQUEST_BODY ??
    ({} as RequestFlexCubeBody);
  return {
    debit: {
      fullName: body.ORDERING_CUSTOMER2 ?? null,
      branch:
        data.debit_account_branch ??
        body.CHARGE_AC_BRN ??
        body.TXN_BRANCH ??
        null,
      currency: data.currency ?? body.DR_AC_CCY ?? null,
    },
    beneficiary: {
      fullName: body.BENEFICIARY2 ?? data.beneficiary_name ?? null,
      branch: data.credit_account_branch ?? null,
      currency: data.currency ?? body.CR_AC_CCY ?? null,
    },
  };
}

/**
 * Libellé du type de transaction : `typeLabels` (fournis par l'appelant,
 * déjà traduits) prioritaire, sinon la clé brute formatée.
 */
export function formatTransactionType(
  type: TransactionType | undefined,
  t: TranslateFn,
  typeLabels?: Record<string, string>
): string {
  if (!type) return t("identifyTransactionWaiting");
  return typeLabels?.[type] ?? formatKey(type);
}

export const STATUS_CONFIG: Record<
  string,
  { labelKey: TFunctionType; color: string }
> = {
  INVALID_TRANSACTION: {
    labelKey: "invalidTransaction",
    color: "bg-warning-muted text-warning",
  },
  APPROVAL_REQUIRED: {
    labelKey: "awaitingApproval",
    color: "bg-warning-muted text-warning",
  },
  PRE_APPROVED: {
    labelKey: "preApproved",
    color: "bg-info-muted text-info",
  },
  PROCESSED: {
    labelKey: "processed",
    color: "bg-success-muted text-success",
  },
  FAILED: {
    labelKey: "failed",
    color: "bg-destructive/10 text-destructive",
  },
  INSUFFICIENT_FUNDS: {
    labelKey: "insufficientFunds",
    color: "bg-destructive/10 text-destructive",
  },
  LIMIT_EXCEEDED: {
    labelKey: "limitExceeded",
    color: "bg-destructive/10 text-destructive",
  },
  PENDING: {
    labelKey: "pending",
    color: "bg-warning-muted text-warning",
  },
};

export function getStatusLabelAndColor(
  status: TransactionStatus | undefined,
  t: TranslateFn
): { label: string; color: string } | null {
  if (!status) return null;
  const config = STATUS_CONFIG[status];
  return config
    ? { label: t(config.labelKey), color: config.color }
    : { label: status, color: "" };
}
